/**
 * Синхронный переводчик для беседы за одним телефоном.
 *
 * Одна линия — одна сессия модели, которая слушает микрофон и переводит
 * ВСЁ услышанное в один язык. Речь, уже звучащую на этом языке, модель
 * пропускает. Беседе нужны две линии: на язык заказчика и на язык
 * поставщика, — и каждая сама отсеивает «своего» говорящего. Кто сейчас
 * говорит, угадывать не нужно.
 *
 * Звук уходит в модель напрямую по WebRTC; наш сервер только выдаёт
 * одноразовый ключ сессии. Переведённую речь модель тоже присылает, но мы
 * её не проигрываем: беседа читается глазами, а голос из динамика попал бы
 * обратно в микрофон.
 */
import { apiFetch } from '../sync';

export interface Lang { code: string; name: string; ru: string; hint: string; start: string }

/** Языки, на которые модель умеет переводить. Слышит она около семидесяти. */
export const LANGS: Lang[] = [
  { code: 'en', name: 'English', ru: 'английский', hint: 'Speak — the translation of your partner’s words will appear here', start: 'Waiting to start' },
  { code: 'zh', name: '中文', ru: 'китайский', hint: '请讲话 — 对方的翻译会显示在这里', start: '等待开始' },
  { code: 'ru', name: 'Русский', ru: 'русский', hint: 'Говорите — перевод слов собеседника появится здесь', start: 'Ждём начала' },
  { code: 'hi', name: 'हिन्दी', ru: 'хинди', hint: 'बोलिए — आपके साथी की बातों का अनुवाद यहाँ दिखेगा', start: 'शुरू होने की प्रतीक्षा' },
  { code: 'ja', name: '日本語', ru: 'японский', hint: '話してください — 相手の言葉の翻訳がここに表示されます', start: '開始を待っています' },
  { code: 'ko', name: '한국어', ru: 'корейский', hint: '말씀하세요 — 상대방 말의 번역이 여기에 표시됩니다', start: '시작 대기 중' },
  { code: 'vi', name: 'Tiếng Việt', ru: 'вьетнамский', hint: 'Hãy nói — bản dịch lời của đối phương sẽ hiện ở đây', start: 'Đang chờ bắt đầu' },
  { code: 'id', name: 'Bahasa Indonesia', ru: 'индонезийский', hint: 'Silakan bicara — terjemahan ucapan lawan bicara akan muncul di sini', start: 'Menunggu dimulai' },
  { code: 'es', name: 'Español', ru: 'испанский', hint: 'Hable — la traducción de su interlocutor aparecerá aquí', start: 'Esperando el inicio' },
  { code: 'pt', name: 'Português', ru: 'португальский', hint: 'Fale — a tradução do seu interlocutor aparecerá aqui', start: 'Aguardando o início' },
  { code: 'fr', name: 'Français', ru: 'французский', hint: 'Parlez — la traduction de votre interlocuteur s’affichera ici', start: 'En attente du début' },
  { code: 'de', name: 'Deutsch', ru: 'немецкий', hint: 'Sprechen Sie — die Übersetzung Ihres Gegenübers erscheint hier', start: 'Warten auf den Start' },
  { code: 'it', name: 'Italiano', ru: 'итальянский', hint: 'Parli — la traduzione del suo interlocutore apparirà qui', start: 'In attesa di iniziare' },
];

export function langOf(code: string): Lang {
  return LANGS.find((l) => l.code === code) ?? LANGS[0];
}

/** Один микрофон на обе линии: второй запрос к микрофону на iPhone глушит первый. */
export async function openMic(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  });
}

export type LineState = 'connecting' | 'live' | 'lost';

export interface LineEvents {
  /** Очередной кусочек перевода — слово или часть слова. */
  onText: (delta: string) => void;
  onState: (s: LineState) => void;
  /** Отказ, который не лечится переподключением: нет денег, нет ключа. */
  onFatal: (message: string) => void;
}

export class InterpretLine {
  private pc?: RTCPeerConnection;
  private track?: MediaStreamTrack;
  private closed = false;
  private lost = false;

  constructor(private lang: string, private mic: MediaStreamTrack, private on: LineEvents) {}

  async start() {
    this.on.onState('connecting');

    const res = await apiFetch('/api/interpret/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lang: this.lang }),
    });
    const d = await res.json().catch(() => ({})) as { secret?: string; callUrl?: string; error?: string };
    if (!res.ok || !d.secret || !d.callUrl) {
      // 402 и 503 — это не обрыв связи: повторять бесполезно, надо сказать.
      if (res.status === 402 || res.status === 503 || res.status === 400) {
        this.on.onFatal(d.error ?? 'Переводчик недоступен.');
        return;
      }
      throw new Error(d.error ?? `сервер ответил ${res.status}`);
    }
    if (this.closed) return;

    const pc = new RTCPeerConnection();
    this.pc = pc;
    // Своя копия дорожки: остановка одной линии не должна глушить другую.
    this.track = this.mic.clone();
    pc.addTrack(this.track, new MediaStream([this.track]));

    const events = pc.createDataChannel('oai-events');
    events.onmessage = ({ data }) => {
      let ev: { type?: string; delta?: unknown; error?: { message?: string } };
      try { ev = JSON.parse(String(data)); } catch { return; }
      if (ev.type === 'session.output_transcript.delta' && typeof ev.delta === 'string') {
        this.on.onText(ev.delta);
      } else if (ev.type === 'error') {
        console.warn('interpret', this.lang, ev.error?.message);
      }
    };

    pc.onconnectionstatechange = () => {
      if (this.closed) return;
      if (pc.connectionState === 'connected') this.on.onState('live');
      // «disconnected», а следом «failed» — это один обрыв, а не два:
      // иначе экран переподключит линию дважды.
      if (!this.lost && (pc.connectionState === 'failed' || pc.connectionState === 'disconnected' || pc.connectionState === 'closed')) {
        this.lost = true;
        this.on.onState('lost');
      }
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    const answer = await fetch(d.callUrl, {
      method: 'POST',
      headers: { Authorization: `Bearer ${d.secret}`, 'Content-Type': 'application/sdp' },
      body: offer.sdp,
    });
    const sdp = await answer.text();
    if (!answer.ok) throw new Error(`модель ответила ${answer.status}: ${sdp.slice(0, 200)}`);
    if (this.closed) return;
    await pc.setRemoteDescription({ type: 'answer', sdp });
  }

  stop() {
    this.closed = true;
    try { this.pc?.close(); } catch { /* уже закрыта */ }
    this.track?.stop();
    this.pc = undefined;
    this.track = undefined;
  }
}

/**
 * Склейка кусочков перевода в строку.
 *
 * Модель присылает куски текста вместе со своими пробелами: « Ла», «д», «но».
 * Их надо сшивать как есть. Правило из примера OpenAI — вставлять пробел,
 * если его нет на стыке, — рвало русские слова на слоги («Л ад но», «вид ишь»):
 * так было на первой живой беседе 26.09. Срезаем только пробел в начале строки.
 */
export function joinDelta(current: string, delta: string): string {
  return current ? current + delta : delta.replace(/^\s+/, '');
}
